/**
 * La dashboard di partenza di ogni ruolo.
 *
 * E' quella che vede chi non ha mai personalizzato, e quella a cui si torna
 * con "Ripristina". Contiene tutto quello che la vecchia pagina iniziale
 * mostrava a quel ruolo (impegni, bozze degli abbonamenti, scadenze, azioni
 * rapide), piu' i numeri utili al suo lavoro. Le disposizioni sono pensate
 * per non lasciare buchi in mezzo alla griglia, larga o stretta (lo
 * controlla un test).
 *
 * Gli id sono fissi (tipo-1, tipo-2...): la stessa base da' sempre la stessa
 * disposizione.
 */

import type { UserRole } from '../../types/roles';
import { CATALOGO, ruoloVede, type MappaImpostazioni, type Taglia, type TipoWidget } from './catalogo';
import { VERSIONE_LAYOUT, type LayoutDashboard, type WidgetIstanza } from './layout';

interface Voce<T extends TipoWidget = TipoWidget> {
  tipo: T;
  taglia?: Taglia;
  impostazioni?: Partial<MappaImpostazioni[T]>;
}

const ANNO = { impostazioni: { periodo: 'anno' as const } };
const MESE = { impostazioni: { periodo: 'mese' as const } };

const BASI: Partial<Record<UserRole, readonly Voce[]>> = {
  admin: [
    { tipo: 'bozze-abbonamenti', taglia: 'M' },
    { tipo: 'rinnovi-abbonamenti', taglia: 'M' },
    { tipo: 'impegni-oggi', taglia: 'L' },
    { tipo: 'lead-da-richiamare', taglia: 'L' },
    { tipo: 'fatturato', taglia: 'S', ...ANNO },
    { tipo: 'da-incassare', taglia: 'S' },
    { tipo: 'ricavi-ricorrenti', taglia: 'S' },
    { tipo: 'nuovi-lead', taglia: 'S' },
    { tipo: 'andamento-mensile', taglia: 'XL' },
    { tipo: 'azioni-rapide', taglia: 'M' },
    { tipo: 'pipeline-lead', taglia: 'M' },
  ],
  amministrazione: [
    { tipo: 'bozze-abbonamenti', taglia: 'M' },
    { tipo: 'rinnovi-abbonamenti', taglia: 'M' },
    { tipo: 'fatturato', taglia: 'S', ...ANNO },
    { tipo: 'fatturato', taglia: 'S', ...MESE },
    { tipo: 'da-incassare', taglia: 'S' },
    { tipo: 'ricavi-ricorrenti', taglia: 'S' },
    { tipo: 'fatture-scadute', taglia: 'L' },
    { tipo: 'impegni-oggi', taglia: 'L' },
    { tipo: 'andamento-mensile', taglia: 'XL' },
    { tipo: 'azioni-rapide', taglia: 'M' },
  ],
  commerciale: [
    { tipo: 'impegni-oggi', taglia: 'L' },
    { tipo: 'lead-da-richiamare', taglia: 'L' },
    { tipo: 'nuovi-lead', taglia: 'S' },
    { tipo: 'offerte-aperte', taglia: 'S' },
    { tipo: 'fatturato', taglia: 'S', ...MESE },
    { tipo: 'ultima-campagna', taglia: 'S' },
    { tipo: 'bozze-abbonamenti', taglia: 'M' },
    { tipo: 'rinnovi-abbonamenti', taglia: 'M' },
    { tipo: 'pipeline-lead', taglia: 'M' },
    { tipo: 'azioni-rapide', taglia: 'M' },
  ],
  sales: [
    { tipo: 'impegni-oggi', taglia: 'L' },
    { tipo: 'lead-da-richiamare', taglia: 'L' },
    { tipo: 'nuovi-lead', taglia: 'S' },
    { tipo: 'offerte-aperte', taglia: 'S' },
    { tipo: 'rinnovi-abbonamenti', taglia: 'M' },
    { tipo: 'pipeline-lead', taglia: 'M' },
    { tipo: 'azioni-rapide', taglia: 'M' },
  ],
};

/** Per i ruoli senza una base propria (lettore, magazzino): l'essenziale. */
const BASE_MINIMA: readonly Voce[] = [
  { tipo: 'impegni-oggi', taglia: 'L' },
  { tipo: 'azioni-rapide', taglia: 'M' },
];

export function layoutPredefinito(ruolo: UserRole | null): LayoutDashboard {
  const voci = (ruolo && BASI[ruolo]) || BASE_MINIMA;
  const conteggio = new Map<TipoWidget, number>();
  const widget: WidgetIstanza[] = [];

  for (const voce of voci) {
    if (!ruoloVede(voce.tipo, ruolo)) continue;
    const def = CATALOGO[voce.tipo];
    const n = (conteggio.get(voce.tipo) ?? 0) + 1;
    conteggio.set(voce.tipo, n);
    widget.push({
      id: `${voce.tipo}-${n}`,
      tipo: voce.tipo,
      taglia: voce.taglia && def.taglie.includes(voce.taglia) ? voce.taglia : def.tagliaPredefinita,
      impostazioni: def.leggiImpostazioni({ ...def.impostazioniPredefinite, ...voce.impostazioni }),
    });
  }

  return { versione: VERSIONE_LAYOUT, widget };
}
